import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Building2, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { apiFetch } from '../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [invite, setInvite] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | accepted | error
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      if (!token) { setStatus('error'); setError('This invite link is missing its token.'); return; }
      try {
        const r = await fetch(`${API_URL}/api/invites/${encodeURIComponent(token)}`);
        const d = await r.json();
        if (!r.ok) {
          setStatus('error');
          setError(typeof d.detail === 'string' ? d.detail : 'Invite not found or expired.');
          return;
        }
        setInvite(d);
        // Already signed in? Accept immediately.
        const me = await apiFetch(`${API_URL}/api/auth/me`);
        if (me.ok) {
          const acc = await apiFetch(`${API_URL}/api/invites/${encodeURIComponent(token)}/accept`, { method: 'POST' });
          const ad = await acc.json();
          if (acc.ok) {
            setStatus('accepted');
            setTimeout(() => { window.location.href = '/dashboard'; }, 1800);
          } else {
            setStatus('ready');
            setError(typeof ad.detail === 'string' ? ad.detail : '');
          }
        } else {
          setStatus('ready');
        }
      } catch {
        setStatus('error');
        setError('Could not load this invitation. Please try again.');
      }
    })();
  }, [token]);

  const roleLabel = (invite?.role || 'member').replace(/_/g, ' ');

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6" data-testid="accept-invite-page">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-slate-900 p-8 text-center space-y-5">
        {status === 'loading' && (
          <div className="flex flex-col items-center gap-3 py-8">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
            <p className="text-slate-300 text-sm">Loading your invitation…</p>
          </div>
        )}

        {status === 'error' && (
          <>
            <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto" />
            <h1 className="text-xl font-semibold text-white">Invitation problem</h1>
            <p className="text-slate-400 text-sm" data-testid="invite-error-text">{error}</p>
            <Button onClick={() => navigate('/login')} className="bg-emerald-600 hover:bg-emerald-700 w-full" data-testid="invite-go-login-btn">
              Go to sign in
            </Button>
          </>
        )}

        {status === 'accepted' && (
          <>
            <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto" />
            <h1 className="text-xl font-semibold text-white">You're in!</h1>
            <p className="text-slate-400 text-sm">
              You've joined <strong className="text-white">{invite?.workspace_name}</strong>. Taking you to your dashboard…
            </p>
          </>
        )}

        {status === 'ready' && invite && (
          <>
            <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto">
              <Building2 className="h-7 w-7 text-emerald-400" />
            </div>
            <h1 className="text-xl font-semibold text-white">
              Join {invite.workspace_name}
            </h1>
            <p className="text-slate-400 text-sm">
              <strong className="text-white">{invite.inviter_name}</strong> invited you to join{' '}
              <strong className="text-white">{invite.workspace_name}</strong> as{' '}
              <span className="capitalize">{roleLabel}</span>.
            </p>
            <p className="text-slate-500 text-xs">
              Use the email address <strong className="text-slate-300">{invite.email}</strong> — you'll be added to the workspace automatically.
            </p>
            {error && <p className="text-amber-400 text-xs" data-testid="invite-accept-error">{error}</p>}
            <div className="space-y-2 pt-2">
              <Button
                onClick={() => navigate(`/login?mode=register&email=${encodeURIComponent(invite.email)}`)}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="invite-register-btn"
              >
                Create my account
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/login?email=${encodeURIComponent(invite.email)}`)}
                className="w-full border-white/20 text-slate-200 hover:bg-white/10"
                data-testid="invite-signin-btn"
              >
                I already have an account — Sign in
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
